import React, { useState, useEffect, useMemo } from 'react';
import axios from "axios";
import Select from 'react-select';
import Swal from "sweetalert2";
import Parser from 'html-react-parser';
import { Link, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../api";
import Loader from "./Loader";  // Assuming you have a Loader component
import Skeleton from "react-loading-skeleton"; // Import Skeleton

const LedgerComp = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const authToken = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    const [projects, setProjects] = useState([]); // Array to store all projects
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        firm: "",
        projectId: "",
        clientId: "",
        startDate: "",
        endDate: "",
    });
    const [tableData, setTableData] = useState([]);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        user_id: localStorage.getItem('userId'),
    };

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/project`, { headers });
            setProjects(response.data);
        } catch (error) {
            console.error("Error fetching projects:", error);
        }
    };
    const fetchClients = async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/client`, { headers });
            setClients(data);
        } catch (error) {
            console.error('Error fetching clients:', error.response?.data || error.message);
        }
    };


    const fetchLedger = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/ledger?firm=${formData.firm}&project_id=${formData.projectId}&client_id=${formData.clientId}&start_date=${formData.startDate}&end_date=${formData.endDate}`,
                { headers }
            );
            setData(response.data);
        } catch (err) {
            setError('Error fetching ledger data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchClients();
        // fetchLedger();
    }, []);

    const projectOptions = useMemo(
        () =>
            projects.map((project) => ({
                value: project.id,
                label: project.project_name,
            })),
        [projects]
    );

    const clientOptions = useMemo(
        () =>
            clients.map((client) => ({
                value: client.id,
                label: client.full_name,
            })),
        [clients]
    );
    // Handler for react-select components

    const handleInputChange = (selectedOption, actionMeta) => {
        setFormData((prevData) => ({
            ...prevData,
            [actionMeta.name]: selectedOption ? selectedOption.value : '',
        }));
    };

    useEffect(() => {
        fetchLedger();
    }, [formData]);


    const deleteLedger = async (rowId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${API_BASE_URL}/api/ledger/${rowId}`, { headers });
                    Swal.fire('Deleted!', 'The ledger entry has been deleted.', 'success');
                    fetchLedger(); // Refresh the ledger data
                } catch (err) {
                    console.error("API error:", err.response ? err.response.data : err);
                    Swal.fire('Error!', 'Something went wrong while deleting the entry.', 'error');
                }
            }
        });
    };

    const toCamelCase = (str) => {
        const camelCased = str.replace(/_([a-z])/g, (match, group1) => group1.toUpperCase());
        return camelCased.charAt(0).toUpperCase() + camelCased.slice(1);
    };
    
    const renderLedgerTableTotal = () => {
        const totalCredit = filteredData.reduce(
            (sum, row) => sum + (Number(row.credit) || 0),
            0
        );

        const totalDebit = filteredData.reduce(
            (sum, row) => sum + (Number(row.debit) || 0),
            0
        );

        return (
            <tfoot>
                <tr>
                    <td colSpan={7}></td>
                    <td><b>{formatINR(totalDebit)}</b></td>
                    <td><b>{formatINR(totalCredit)}</b></td>
                </tr>
                <tr>
                    <td colSpan={8}></td>
                    <td><b>{formatINR(totalDebit - totalCredit)}</b></td>
                </tr>
            </tfoot>
        );
    };

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, itemsPerPage]);

    const handleSort = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortValue = (entry, key) => {
        switch (key) {
            case "retainer":
                return entry.retainer ? 1 : 0;
            case "client":
                return entry.client?.full_name || "";
            case "firm":
                return entry.firm || "";
            case "date":
                return entry.date || "";
            case "title":
                return entry.title || "";
            case "debit":
                return Number(entry.debit) || 0;
            case "credit":
                return Number(entry.credit) || 0;
            default:
                return "";
        }
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }
        return sortConfig.direction === "asc"
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const filteredData = data.filter((ledger) =>
        [
            ledger.project.project_name,
            ledger.client.full_name,
            ledger.firm,
            ledger.date,
            ledger.title,
            ledger.start_date,
            ledger.end_date,
            ledger.debit,
            ledger.credit,
            ledger.renewal_month,
        ]
            .filter(Boolean)// Prevent errors from null values
            .some((field) =>
                field.toString().toLowerCase().includes(search.toLowerCase())
            )
    );

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const valueA = getSortValue(a, sortConfig.key);
        const valueB = getSortValue(b, sortConfig.key);

        if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = sortedData.slice(startIndex, startIndex + parseInt(itemsPerPage));

    const formatINR = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
        }).format(amount);
    };

    if (loading) {
        return <div> <p>Loading..</p> </div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="content">

            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                <div className="my-auto mb-2 text-start">
                    <div className="pagetitle">
                        <h1>Ledger</h1>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="#">Home</a>
                                </li>
                                <li className="breadcrumb-item active">Ledger</li>
                            </ol>
                        </nav>
                    </div>
                </div>
                <Link to={`/dashboard/add-ledger`} title="Add Ledger"
                    className="btn btn-primary d-flex align-items-center">
                    <i className="fa fa-plus-circle me-2"></i>New Entry
                </Link>
            </div>

            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                <div className="row">

                    <div className="col-md-3 mb-3">
                        <label className="form-label">Firm</label>
                        <select
                            name="firm"
                            value={formData.firm}
                            onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
                            className="form-select"
                        >
                            <option value="">Select</option>
                            <option value="BHAVIK">BHAVIK</option>
                            <option value="BRIJESH">BRIJESH</option>
                            <option value="CK_JAYDEEP">CK_JAYDEEP</option>
                            <option value="SAI LLP">SAI LLP</option>
                            <option value="SAI OLD">SAI OLD</option>
                            <option value="REALDO">REALDO</option>
                            <option value="MEGHA">MEGHA</option>
                            <option value="CASH">CASH</option>
                        </select>
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Client</label>
                        <Select
                            options={clientOptions}
                            name="clientId"
                            value={clientOptions.find((option) => option.value === formData.clientId)}
                            onChange={handleInputChange}
                            placeholder="Select a Client"
                            isClearable
                        />
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Project Name</label>
                        <Select
                            options={projectOptions}
                            name="projectId"
                            value={projectOptions.find((option) => option.value === formData.projectId)}
                            onChange={handleInputChange}
                            placeholder="Select a Project"
                            isClearable
                        />
                    </div>
                    <div className="clearfix"></div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Start Date</label>
                        <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                            className="form-control"
                        />
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">End Date</label>
                        <input
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                            className="form-control"
                        />
                    </div>
                </div>
            </div>
            {/* Show Loader when loading */}
            {loading ? (

                <div className="skeleton-loader-table">
                    <table className="table table-bordered dataTable" id="datatable">
                        <thead>
                            <tr>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                            </tr>
                        </thead>
                    </table>
                </div>
            ) : (
                <>
                    <div className="card-body p-0">
                        <div className="table-responsive custom-datatable-filter">
                            <div className="row mt-2 justify-content-between mb-3">
                                <div className="d-flex justify-content-between align-items-center dt-layout-start col-md-auto me-auto">
                                    <label htmlFor="dt-length-0"> entries per page</label>
                                    <select aria-controls="datatable" className="form-select form-select-sm" id="dt-length-0" value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(e.target.value)}>
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                                <div className="d-md-flex justify-content-between align-items-center dt-layout-end col-md-auto ms-auto">
                                    <label htmlFor="dt-search-0" className="form-label me-2 visually-hidden">Search:</label>
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="p-2 border rounded mb-4 w-full"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <table className="table table-bordered dataTable" id="datatable">
                                <thead>
                                    <tr>
                                        <th>Action</th>
                                        <th>SR.No</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("retainer")}>Retainer {renderSortIcon("retainer")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("client")}>Client {renderSortIcon("client")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("firm")}>FIRM {renderSortIcon("firm")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("date")}>Date {renderSortIcon("date")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("title")}>Title {renderSortIcon("title")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("debit")}>Debit {renderSortIcon("debit")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("credit")}>Credit {renderSortIcon("credit")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.map((entry, index) => (
                                        <tr key={entry.id || index} id={`ledger${entry.id}`}>


                                            <td>
                                                {
                                                    entry.parent_id == null ?
                                                        <Link to={`/dashboard/add-ledger/${entry.project_id}/${entry.client_id}/${entry.id}`} title="Add Payment" className='mr-3'> Add ₹</Link> : ""
                                                }
                                                <Link to={`/dashboard/edit-ledger/${entry.id}`} title="Edit">
                                                    <i className="fas fa-edit text-primary ms-3"></i>
                                                </Link>
                                                <i
                                                    className="fas fa-trash text-danger ms-3"
                                                    title="Delete"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => deleteLedger(entry.id)}
                                                ></i>
                                            </td>
                                            <td> {((currentPage - 1) * itemsPerPage) + (index + 1)}</td>
                                            <td>{Parser(entry.retainer == 1 && entry.credit == 0 ? "Retainer" : "")}</td>
                                            <td>{Parser(entry.client != null ? entry.client.full_name : "")}{Parser(entry.project != null ? "<br>" + entry.project.project_name : "")}</td>
                                            <td>{entry.firm}</td>
                                            <td>{entry.date}</td>
                                            <td>{toCamelCase(entry.title)}{Parser(entry.start_date != null ? '<br>Start Date: ' + entry.start_date : "")}{Parser(entry.start_date != null ? '<br>End Date: ' + entry.end_date : "")}{Parser(entry.renewal_month != null ? "<br>Renewal time : " + entry.renewal_month + " month" : "")}{Parser(entry.parent != null ? "<br><a href='#ledger" + entry.parent.id + "'><small>" + entry.parent.title + "</small></a>" : "")}</td>
                                            <td>{formatINR(entry.debit)}</td>
                                            <td>{formatINR(entry.credit)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                {renderLedgerTableTotal()}
                            </table>
                            <div className="flex justify-center mt-4">
                                <button
                                    className="px-3 py-1 mx-1 border rounded disabled:opacity-50"
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
                                <button
                                    className="px-3 py-1 mx-1 border rounded disabled:opacity-50"
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LedgerComp;
